<script lang="ts">
  import { onDestroy, onMount, tick } from 'svelte';
  import type { ECharts, EChartsOption, SeriesOption } from 'echarts';
  import type {
    CalculateResponse,
    ChartPoint,
    DiagramResponse,
    EnthalpyUnit,
    PressureUnit,
    TemperatureUnit,
  } from './api';

  export let diagram: DiagramResponse | null = null;
  export let result: CalculateResponse | null = null;
  export let pressureUnit: PressureUnit = 'psig';
  export let enthalpyUnit: EnthalpyUnit = 'kJ_kg';
  export let temperatureUnit: TemperatureUnit = 'F';

  let container: HTMLDivElement;
  let chart: ECharts | null = null;
  let echartsModule: typeof import('echarts') | null = null;

  const enthalpyLabel = (u: EnthalpyUnit) => (u === 'kJ_kg' ? 'kJ/kg' : 'Btu/lb');
  const pressureLabel = (u: PressureUnit) => u;
  const tempSuffix = (u: TemperatureUnit) => (u === 'F' ? '°F' : '°C');

  function addDeltaSegment(
    series: SeriesOption[],
    opts: {
      name: string;
      measured: ChartPoint;
      saturation: ChartPoint;
      deltaLabel: string;
      color: string;
    },
  ) {
    const { name, measured, saturation, deltaLabel, color } = opts;
    series.push(
      {
        name,
        type: 'line',
        showSymbol: false,
        z: 3,
        lineStyle: { type: 'dashed', color, width: 2 },
        data: [
          [saturation.h_display, saturation.p_display],
          [measured.h_display, measured.p_display],
        ],
        label: {
          show: true,
          position: 'middle',
          formatter: deltaLabel,
          color: '#212529',
          backgroundColor: 'rgba(255,255,255,0.85)',
          padding: [2, 4],
          borderRadius: 2,
        },
      } as unknown as SeriesOption,
      {
        name: saturation.label,
        type: 'scatter',
        z: 4,
        symbol: 'diamond',
        symbolSize: 11,
        itemStyle: { color: '#fff', borderColor: color, borderWidth: 2 },
        data: [[saturation.h_display, saturation.p_display]],
        label: {
          show: true,
          position: 'bottom',
          formatter: saturation.label,
          color: '#495057',
          fontSize: 10,
        },
      } as SeriesOption,
    );
  }

  function buildOption(): EChartsOption {
    if (!diagram) return {};

    const liquid = diagram.liquid_line.map((p) => [p.h_display, p.p_display]);
    const vapor = diagram.vapor_line.map((p) => [p.h_display, p.p_display]);
    const crit = diagram.critical;

    const series: SeriesOption[] = [
      {
        name: 'Saturated liquid',
        type: 'line',
        showSymbol: false,
        smooth: true,
        lineStyle: { color: '#6c757d', width: 2 },
        data: liquid,
      },
      {
        name: 'Saturated vapor',
        type: 'line',
        showSymbol: false,
        smooth: true,
        lineStyle: { color: '#adb5bd', width: 2 },
        data: vapor,
      },
    ];

    if (crit) {
      series.push({
        name: 'Critical point',
        type: 'scatter',
        symbolSize: 10,
        itemStyle: { color: '#495057' },
        data: [[crit.h_display, crit.p_display]],
      });
    }

    if (result) {
      const low = result.low.chart_point;
      const high = result.high.chart_point;
      const lowSat = result.low.sat_chart_point;
      const highSat = result.high.sat_chart_point;
      const tSuffix = tempSuffix(temperatureUnit);

      if (result.low.superheat_display != null) {
        addDeltaSegment(series, {
          name: 'Superheat',
          measured: low,
          saturation: lowSat,
          deltaLabel: `Superheat ${result.low.superheat_display.toFixed(1)}${tSuffix}`,
          color: '#0d6efd',
        });
      }

      if (result.high.subcooling_display != null) {
        addDeltaSegment(series, {
          name: 'Subcooling',
          measured: high,
          saturation: highSat,
          deltaLabel: `Subcooling ${result.high.subcooling_display.toFixed(1)}${tSuffix}`,
          color: '#dc3545',
        });
      }

      series.push(
        {
          name: 'Cycle link',
          type: 'line',
          showSymbol: false,
          z: 1,
          lineStyle: { type: 'dashed', color: '#868e96', width: 1 },
          data: [
            [low.h_display, low.p_display],
            [high.h_display, high.p_display],
          ],
        },
        {
          name: low.label,
          type: 'scatter',
          z: 5,
          symbolSize: 14,
          itemStyle: { color: '#0d6efd' },
          data: [[low.h_display, low.p_display]],
          label: {
            show: true,
            position: 'top',
            formatter: low.label,
            color: '#212529',
          },
        },
        {
          name: high.label,
          type: 'scatter',
          z: 5,
          symbolSize: 14,
          itemStyle: { color: '#dc3545' },
          data: [[high.h_display, high.p_display]],
          label: {
            show: true,
            position: 'top',
            formatter: high.label,
            color: '#212529',
          },
        },
      );
    }

    const legendNames = series
      .map((s) => (typeof s === 'object' && s && 'name' in s ? s.name : undefined))
      .filter((name): name is string => typeof name === 'string');
    const legendSplit = Math.ceil(legendNames.length / 2);
    const legendRow = (data: string[], bottom: number) => ({
      type: 'plain' as const,
      bottom,
      left: 'center' as const,
      orient: 'horizontal' as const,
      data,
      itemGap: 14,
      itemWidth: 18,
      itemHeight: 10,
      textStyle: { fontSize: 11, color: '#495057' },
    });

    return {
      backgroundColor: '#fff',
      grid: { left: 56, right: 24, top: 16, bottom: 102 },
      tooltip: {
        trigger: 'item',
        formatter: (params: unknown) => {
          const p = params as { seriesName: string; value: [number, number] };
          const [h, press] = p.value;
          return `${p.seriesName}<br/>h: ${h.toFixed(2)} ${enthalpyLabel(enthalpyUnit)}<br/>P: ${press.toFixed(2)} ${pressureLabel(pressureUnit)}`;
        },
      },
      legend: [
        legendRow(legendNames.slice(0, legendSplit), 38),
        legendRow(legendNames.slice(legendSplit), 10),
      ],
      xAxis: {
        type: 'value',
        name: `Enthalpy (${enthalpyLabel(enthalpyUnit)})`,
        nameLocation: 'middle',
        nameGap: 32,
      },
      yAxis: {
        type: 'log',
        name: `Pressure (${pressureLabel(pressureUnit)})`,
        logBase: 10,
        minorSplitLine: { show: false },
      },
      series,
    };
  }

  function renderChart() {
    if (!chart || !diagram) return;
    chart.resize();
    chart.setOption(buildOption(), { notMerge: true });
  }

  let renderAfterLayout: Promise<void> | null = null;

  function scheduleRender() {
    if (!chart || !diagram) return;
    if (renderAfterLayout) return;
    renderAfterLayout = tick().then(() => {
      renderAfterLayout = null;
      renderChart();
    });
  }

  async function initChart() {
    if (!container) return;
    echartsModule = await import('echarts');
    chart = echartsModule.init(container);
    scheduleRender();
  }

  function handleResize() {
    chart?.resize();
  }

  onMount(() => {
    initChart();
    window.addEventListener('resize', handleResize);
  });

  onDestroy(() => {
    window.removeEventListener('resize', handleResize);
    chart?.dispose();
    chart = null;
  });

  $: diagram, result, pressureUnit, enthalpyUnit, temperatureUnit, chart, scheduleRender();
</script>

<div class="ph-diagram" bind:this={container} role="img" aria-label="Pressure-enthalpy diagram"></div>

<style>
  .ph-diagram {
    width: 100%;
    height: min(480px, 55vh);
    min-height: 320px;
  }
</style>
